import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Box, IconButton, Typography } from "@mui/material";
import {
  TimeField,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { DefaultCheckBox, PeriodTimes, WeekDay } from 'src/components';
import { TooltipIcon } from 'src/Icons';
import { CustomTooltip } from 'src/styledComponents';
import { gettingWorkingTimes } from 'src/utils/helpers';

import { SectionHeader } from ".";

interface PeriodProps {
  label: string;
  isSelected: boolean;
  onClick: (value: string) => void;
  disabled: boolean;
}

const Period = ({ label, isSelected, onClick, disabled }: PeriodProps) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "32px",
        borderRadius: "6px",
        padding: "0px 12px",
        cursor: disabled ? "default" : "pointer",
        backgroundColor:
          disabled && isSelected
            ? "#D6D6DB"
            : isSelected
              ? "#363636"
              : "transparent",
        color:
          (disabled && isSelected) || (!disabled && isSelected)
            ? "white"
            : disabled && !isSelected
              ? "#D6D6DB"
              : "#363636",
      }}
      onClick={() => !disabled && onClick(label)}
    >
      <Typography
        sx={{
          fontWeight: "500",
          fontSize: "14px",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

interface TimePeriodPickerProps {
  selectedPeriod: string;
  onChange: (newValue: string) => void;
  disabled: boolean;
}

const TimePeriodPicker = ({
  selectedPeriod,
  onChange,
  disabled,
}: TimePeriodPickerProps) => {
  // @todo get proper date from ampm and time

  const handleChangePeriod = (label: string) => {
    label !== selectedPeriod && onChange(label);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        marginLeft: "8px",
      }}
    >
      <Period
        onClick={handleChangePeriod}
        label="AM"
        disabled={disabled}
        isSelected={selectedPeriod === "AM"}
      />
      <Period
        onClick={handleChangePeriod}
        label="PM"
        disabled={disabled}
        isSelected={selectedPeriod === "PM"}
      />
    </Box>
  );
};

interface TimePickerProps {
  time: string;
  onChanged: (newValue: string) => void;
  disabled: boolean;
  disabledText?: string;
}

export const TimePicker = ({
  time,
  onChanged,
  disabled,
  disabledText,
}: TimePickerProps) => {
  const [value, setValue] = useState(dayjs(`2022-04-17T${time}`));
  const [timePeriod, setTimePeriod] = useState(value.format("A"));

  const getCurrentTime = (time: any, period: string) => {
    const [hours, minutes] = time.format("hh:mm").split(":");

    if (period === "PM") {
      return `${hours != 12 ? +hours + 12 : "12"}:${minutes}`;
    } else {
      return `${hours != 12 ? +hours : "00"}:${minutes}`;
    }
  };

  const handleValueChange = (newValue: any) => {
    onChanged(getCurrentTime(newValue, timePeriod));
    setValue(newValue);
  };

  const handleChangePeriod = (newPeriod: string) => {
    onChanged(getCurrentTime(value, newPeriod));
    setTimePeriod(newPeriod);
  };

  const handleKeyDown = (event: any) => {
    setTimeout(() => {
      if (event.key === 'Delete' || event.key === 'Backspace') {
        const [hoursPart, minutesPart] = event.target.value.split(':');

        handleValueChange(hoursPart === 'hh' ? value.set('hours', 0) : value.set('minutes', 0));
        event.preventDefault();
      }
    }, 1);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        {disabled ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #D6D6DB",
              width: "62px",
              height: "32px",
              borderRadius: "6px",
              cursor: "default",
            }}
          >
            <Typography
              sx={{
                color: "#9D9D9D",
                fontWeight: "400",
                fontSize: "14px",
              }}
            >
              {disabledText}
            </Typography>
          </Box>
        ) : (
          <TimeField
            value={value}
            ampm={false}
            format="h:mm"
            onChange={handleValueChange}
            shouldRespectLeadingZeros
            onKeyDown={(e: any) => handleKeyDown(e)}
            sx={{
              width: "62px",
              borderRadius: "6px",

              "& .MuiInputBase-input": {
                textAlign: "center",
                height: "32px",
                padding: "0px",
                fontSize: "14px",
                color: "#080808",
                borderRadius: "6px",
              },

              "&:hover": {
                boxShadow: "none",
              },
              [`& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,
             & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline,
             & .MuiOutlinedInput-notchedOutline`]: {
                border: "1px solid #D6D6DB",
                borderRadius: "6px",
                boxShadow: "none",
              },
            }}
          />
        )}

        <TimePeriodPicker
          selectedPeriod={timePeriod}
          onChange={handleChangePeriod}
          disabled={disabled}
        />
      </Box>
    </LocalizationProvider>
  );
};

interface WeekDayPickerProps {
  disabled: boolean;
  label: string;
  isChecked: boolean;
  onChangeChecked: (newValue: boolean) => void;
  from: string;
  onFromChanged: (newValue: string) => void;
  to: string;
  onToChanged: (newValue: string) => void;
}

export const WeekDayPicker = ({
  disabled,
  label,
  isChecked,
  onChangeChecked,
  from,
  onFromChanged,
  to,
  onToChanged,
}: WeekDayPickerProps) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flex: 1,
        border: "1px solid #D6D6DB",
        borderTop: "none",

        "&:first-of-type": {
          borderTop: "1px solid #D6D6DB",
          borderTopLeftRadius: "6px",
          borderTopRightRadius: "6px",

          "&>.leftBlock": {
            borderTopLeftRadius: "6px",
          },
        },
        "&:last-child, &:last-child>.leftBlock": {
          borderBottomLeftRadius: "6px",
          borderBottomRightRadius: "6px",
        },
      }}
    >
      <Box
        className="leftBlock"
        sx={{
          display: "flex",
          alignItems: "center",
          height: "46px",
          minWidth: 170,
          paddingLeft: "16px",
          backgroundColor: isChecked ? "#F3F3F3" : "#FAFAFA",
          color: isChecked ? "#1F1F1F" : "#9D9D9D",
          borderRight: "none",
        }}
      >
        <DefaultCheckBox
          disabled={disabled}
          isChecked={isChecked}
          onChange={() => onChangeChecked(!isChecked)}
        />

        <Typography
          sx={{
            fontWeight: "500",
            fontSize: "14px",
            cursor: "default",
          }}
        >
          {label}
        </Typography>
        {disabled &&
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          flex: 1,
          marginRight: '10px'
        }}>
          <CustomTooltip
            title="The studio is not open that day."
          >
            <IconButton
              className="tooltip-button"
              sx={{
                padding: 0,
                color: '#4F4F4F',
                fontSize: '16px',
                '&:hover': {
                  color: '#BF295C',
                  backgroundColor: 'transparent',
                },
              }}
            >
              <TooltipIcon fontSize="inherit" />
            </IconButton>
          </CustomTooltip>
        </Box>}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0px 16px",
          flex: 1,
        }}
      >
        <TimePicker
          time={from}
          disabled={!isChecked}
          disabledText="Open"
          onChanged={onFromChanged}
        />
        <Typography
          sx={{
            color: isChecked ? "#4F4F4F" : "#9D9D9D",
            cursor: "default",
            fontSize: "14px",
            fontWeight: "400",
          }}
        >
          to
        </Typography>
        <TimePicker
          time={to}
          disabled={!isChecked}
          disabledText="Close"
          onChanged={onToChanged}
        />
      </Box>
    </Box>
  );
};

export const getTimeStr = (
  hours: number | undefined,
  minutes: number | undefined,
  defaultHours = 9,
  defaultMinutes = 0,
) => {
  return dayjs()
    .hour(hours === 0 ? 0 : hours || defaultHours)
    .minute(minutes === 0 ? 0 : minutes || defaultMinutes)
    .format('HH:mm');
};

export const initWeekdays = [{
  label: "Monday",
  key: "1",
}, {
  label: "Tuesday",
  key: "2",
}, {
  label: "Wednesday",
  key: "3",
}, {
  label: "Thursday",
  key: "4",
}, {
  label: "Friday",
  key: "5",
}, {
  label: "Saturday",
  key: "6",
}, {
  label: "Sunday",
  key: "0",
},
];

interface WeekDaySectionProps {
  label?: string;
  daysPeriods?: {
    [key in WeekDay as string]: PeriodTimes
  };
  employeeDaysPeriods?: {
    [key in WeekDay as string]: PeriodTimes
  };
  onChange?: (weekdays: any) => any;
  isEmployeeCreation?: boolean;
}

export const WeekDaySection = React.forwardRef(({
  label,
  daysPeriods,
  employeeDaysPeriods,
  onChange,
  isEmployeeCreation = false,
}: WeekDaySectionProps, ref) => {

  const disableWeekdays = gettingWorkingTimes(daysPeriods);

  // @todo refactor
  const [weekDays, setWeekDays] = useState(initWeekdays.map(({label, key}) => {
    const periods = isEmployeeCreation ? employeeDaysPeriods : daysPeriods;

    const isChecked = !!periods?.[key];
    const isDisabled = isEmployeeCreation && disableWeekdays.some((filteredDay) => filteredDay.key === key);

    return {
      label,
      key,
      checked: isChecked,
      disabled: isDisabled,
      from: getTimeStr(periods?.[key]?.from?.hours, periods?.[key]?.from?.minutes, 9, 0),
      to: getTimeStr(periods?.[key]?.to?.hours, periods?.[key]?.to?.minutes, 19, 0),
    }
  }));

  useEffect(() => {
    onChange && onChange(weekDays)
  }, [onChange, weekDays])

  const handleWeekdaysChange = (newWeekdays: any) => {
    setWeekDays(newWeekdays);
    setTimeout(() => {
      onChange?.(newWeekdays);
    }, 150);
  }

  useImperativeHandle(ref, () => ({
    getDaysPeriods: () => {
      const newPeriods = {} as any;

      weekDays.forEach((day) => {
        if (day.checked) {
          const timeFromStrArr =  day.from.split(':');
          const timeToStrArr =  day.to.split(':');

          newPeriods[day.key] = {
            from: {
              hours: +timeFromStrArr[0],
              minutes: +timeFromStrArr[1],
            },
            to: {
              hours: +timeToStrArr[0],
              minutes: +timeToStrArr[1],
            },
          }
        }
      });

      return newPeriods;
    },
  }));

  const handleChangeChecked = (index: number) => (value: boolean) => {
    const weekDay = weekDays[index];

    const updatedWeekDay = {
      ...weekDay,
      checked: value,
    };
    const newWeekDays = [
      ...weekDays.slice(0, index),
      updatedWeekDay,
      ...weekDays.slice(index + 1),
    ];
    handleWeekdaysChange(newWeekDays);
  };

  const handleFromChanged = (index: number) => (value: string) => {
    const weekDay = weekDays[index];

    const updatedWeekDay = {
      ...weekDay,
      from: value,
    };
    const newWeekDays = [
      ...weekDays.slice(0, index),
      updatedWeekDay,
      ...weekDays.slice(index + 1),
    ];
    handleWeekdaysChange(newWeekDays);
  };

  const handleToChanged = (index: number) => (value: string) => {
    const weekDay = weekDays[index];

    const updatedWeekDay = {
      ...weekDay,
      to: value,
    };
    const newWeekDays = [
      ...weekDays.slice(0, index),
      updatedWeekDay,
      ...weekDays.slice(index + 1),
    ];
    handleWeekdaysChange(newWeekDays);
  };

  return (
    <Box>
      {label && <SectionHeader label={label} />}
      <Box>
        {weekDays.map(({ label, checked, from, to, disabled}, index) => {
          return (
            <WeekDayPicker
              disabled={disabled}
              key={index}
              label={label}
              isChecked={checked}
              onChangeChecked={handleChangeChecked(index)}
              from={from}
              onFromChanged={handleFromChanged(index)}
              to={to}
              onToChanged={handleToChanged(index)}
            />
          );
        })}
      </Box>
    </Box>
  );
});
